import { Alert, AlertIcon, Box, Flex, Skeleton, Text } from '@chakra-ui/react';
import * as _ from 'lodash';
import React, { useMemo } from 'react';

import {
  finleyAssert,
  date as commonDate,
  utils as commonUtils,
} from '@finley-technologies/common';

import {
  ResolvedLoanFragmentGQLType,
  InvoiceItemInputGQLType,
} from '../../../../../types/dashboard-core-gqlTypes.generated.d';
import { useCreateInvoiceContext } from '../../hooks/useCreateInvoice';
import { AccrualsByTAccountMap } from '../utils';
import { CreateInvoiceLineItemsCards } from './CreateInvoiceLineItemsCards';
import { CreateInvoiceLineItemsTable } from './CreateInvoiceLineItemsTable';
import { InfoAlertWithDescription } from 'packages/common-frontend/components/InfoAlertWithDescription';
import { useGetCurrentLiabilityAccountsAndAccrualsForLoanQuery } from 'packages/loans/requests/loans.gql.generated';
import { usePermissionGuard } from '~/core/PermissionGuard';
import { FullDateRangePicker } from '~/packages/common-frontend/components/DateRangePicker';

const CreateLoanInvoiceLineItems = ({
  loan,
  invoiceComponentIndex,
}: {
  loan: ResolvedLoanFragmentGQLType;
  invoiceComponentIndex: number;
}) => {
  const isFullBalancePaymentEnabled =
    usePermissionGuard(
      (context) => context.flags.productsInvoicesFullBalancePaymentPermissions,
    ) === 'allow';

  const {
    invoice,
    invoiceComponentsWithInvoiceItems,
    updateInvoice,
    updateInvoiceComponent,
    updateInvoiceItemsAndTotalsForInvoiceComponent,
  } = useCreateInvoiceContext();

  const invoiceItems = useMemo(
    () => invoiceComponentsWithInvoiceItems[invoiceComponentIndex].invoiceItems,
    [invoiceComponentIndex, invoiceComponentsWithInvoiceItems],
  );

  // if the invoice component only has principal, the product ask is to hide the accrual date selector
  const isPrincipalOnlyInvoiceComponent = useMemo(() => {
    return (
      invoiceItems.length === 1 &&
      invoiceItems[0].accountingAccount.tAccount === 'longTermDebt'
    );
  }, [invoiceItems]);

  const {
    data: accrualsData,
    loading: isAccrualsLoading,
  } = useGetCurrentLiabilityAccountsAndAccrualsForLoanQuery({
    variables: {
      accrualStartDate:
        invoiceComponentsWithInvoiceItems[invoiceComponentIndex]
          .invoiceComponent.accrualStartDate,
      accrualEndDate:
        invoiceComponentsWithInvoiceItems[invoiceComponentIndex]
          .invoiceComponent.accrualEndDate,
      loanId: loan.id,
      inclusiveOfAccrualEndDate: false,
    },
    fetchPolicy: 'cache-and-network',
    onCompleted: (data) => {
      // Accrual data is not available yet or there is none for the selected date range
      if (!data || data.accounts.length === 0) {
        updateInvoice({ field: 'totalAmount', value: 0 });
        updateInvoiceComponent({
          invoiceComponentIndex,
          field: 'totalAmount',
          value: 0,
        });
        updateInvoiceItemsAndTotalsForInvoiceComponent({
          invoiceComponentIndex,
          newInvoiceItemsForInvoiceComponent: [],
        });
        return;
      }

      // Leave the existing invoice items alone if they are present.
      // In places where recalculation of invoice items are needed with accrual refetch,
      // first resetInvoiceComponents()
      if (invoiceItems.length > 0) {
        return;
      }
      // update the default invoice items for the component every time the accrual dates change
      const newInvoiceItems: InvoiceItemInputGQLType[] = _.compact(
        data.accounts.map((accountAccrual) => {
          // cannot pay down an admin agent principal payable.
          // this is a liability account that is used to track the principal amount that is owed to the admin agent
          // and is not borrower-facing.
          if (
            accountAccrual.tAccount.tAccount === 'adminAgentPrincipalPayable'
          ) {
            return null;
          }

          // default to not showing long term debt account, even though it's a valid option
          if (accountAccrual.tAccount.tAccount === 'longTermDebt') {
            return null;
          }

          const {
            id,
            tAccount,
            organizationId,
            creditFacilityId,
          } = accountAccrual.tAccount;

          return {
            accountingAccountId: id,
            amount: commonUtils.jsRound({
              value: accountAccrual.totalAccruedAmount,
              precision: 2,
            }),
            finleyAccruedAmount: commonUtils.jsRound({
              value: accountAccrual.totalAccruedAmount,
              precision: 2,
            }),
            status: 'created',
            accountingAccount: {
              id,
              tAccount,
              organizationId,
              creditFacilityId,
            },
          };
        }),
      );

      updateInvoiceItemsAndTotalsForInvoiceComponent({
        invoiceComponentIndex,
        newInvoiceItemsForInvoiceComponent: newInvoiceItems,
      });
    },
  });

  // Can remove this variable completely once fully allow paying down principal balance
  // prior to completing "Mark Funded" (transferring balance to Admin Agent)
  const areAllComponentsFundedOnLoan = useMemo<boolean>(() => {
    if (isFullBalancePaymentEnabled) {
      return true;
    }
    return loan.funding.requests.every((request) =>
      request.fundingComponents.every(
        (component) => component.fundTransferDate,
      ),
    );
  }, [loan.funding.requests, isFullBalancePaymentEnabled]);

  const totalAccruedAndProjectedOutstandingBalanceAmount = useMemo(() => {
    const total = (accrualsData?.accounts ?? []).reduce<number>(
      (acc, account) => {
        if (
          account.tAccount.tAccount === 'adminAgentPrincipalPayable' ||
          account.tAccount.tAccount === 'longTermDebt'
        ) {
          // eslint-disable-next-line no-param-reassign
          acc += account.totalAccruedAmount;
        }

        return acc;
      },
      0,
    );
    return commonUtils.jsRound({ value: total, precision: 2 });
  }, [accrualsData]);

  const accrualsByTAccountMap = useMemo<AccrualsByTAccountMap>(() => {
    if (!accrualsData) return {};

    return accrualsData.accounts.reduce<AccrualsByTAccountMap>(
      (acc, account) => {
        if (isFullBalancePaymentEnabled) {
          // For UX purposes, borrower (creator of the invoice) does not need to care
          // or know about the "funding" mechanics of the draw request (i.e., Admin
          // Agent Fronting activity doesn't matter to them).
          // Thus, we'll combine "AdminAgentPayable" activity into the "LongTermDebt"
          // to singularly represent the loan's outstanding balance.
          // Noting this will need to be properly handled in the backend due to this
          // adjustment (ideally we use de-coupled "account" representations between FE & BE)
          let adjustTAccount = account.tAccount;
          if (account.tAccount.tAccount === 'adminAgentPrincipalPayable') {
            const longTermDebtAccount = accrualsData.accounts.find(
              (x) => x.tAccount.tAccount === 'longTermDebt',
            );
            finleyAssert(
              longTermDebtAccount,
              'Expected to find Long Term Debt if Admin Agent Payable is present',
            );
            adjustTAccount = longTermDebtAccount.tAccount;
          }

          const existingItem = acc[adjustTAccount.id] ?? {};

          // eslint-disable-next-line no-param-reassign
          acc[adjustTAccount.id] = {
            tAccount: adjustTAccount,
            projectedAccruedAmount: commonUtils.jsRound({
              value:
                (existingItem.projectedAccruedAmount || 0) +
                account.projectedAccruedAmount,
              precision: 2,
            }),
            actualAccruedAmount: commonUtils.jsRound({
              value:
                (existingItem.actualAccruedAmount || 0) +
                account.actualAccruedAmount,
              precision: 2,
            }),
            totalAccruedAmount: commonUtils.jsRound({
              value:
                (existingItem.totalAccruedAmount || 0) +
                account.totalAccruedAmount,
              precision: 2,
            }),
          };

          return acc;
        }

        // cannot pay down an admin agent principal payable.
        // this is a liability account that is used to track the principal amount that is owed to the admin agent
        // and is not borrower-facing.
        if (account.tAccount.tAccount === 'adminAgentPrincipalPayable') {
          return acc;
        }

        // cannot pay down long term debt if not all components are funded
        // can still pay down other tAccounts
        if (
          account.tAccount.tAccount === 'longTermDebt' &&
          !areAllComponentsFundedOnLoan
        ) {
          return acc;
        }

        const tAccountId = account.tAccount.id;

        return {
          ...acc,
          [tAccountId]: {
            tAccount: account.tAccount,
            projectedAccruedAmount:
              (acc[tAccountId]?.projectedAccruedAmount || 0) +
              account.projectedAccruedAmount,
            actualAccruedAmount:
              (acc[tAccountId]?.actualAccruedAmount || 0) +
              account.actualAccruedAmount,
            totalAccruedAmount:
              (acc[tAccountId]?.totalAccruedAmount || 0) +
              account.totalAccruedAmount,
          },
        };
      },
      {},
    );
  }, [accrualsData, areAllComponentsFundedOnLoan, isFullBalancePaymentEnabled]);

  if (isAccrualsLoading || !accrualsData) {
    return <Skeleton height="400px" />;
  }

  return (
    <Flex direction="column">
      {invoice.invoiceType === 'loan' && loan && (
        <CreateInvoiceLineItemsCards
          invoiceComponentIndex={invoiceComponentIndex}
        />
      )}

      {/* Guided Form Step 3 */}
      <Flex direction="column">
        {!isPrincipalOnlyInvoiceComponent && (
          <Box mb="40px" zIndex="dropdown">
            <Text fontWeight={600} mb="16px">
              Accrual Dates
            </Text>
            <FullDateRangePicker
              width="100%"
              onSelectDateRange={(newDateRange) => {
                const [newStartDate, newEndDate] = newDateRange;
                if (newStartDate && newEndDate) {
                  // @dani multi-invoice deprecate
                  updateInvoice({
                    field: 'accrualStartDate',
                    value: commonDate.dateToFullDateString(newStartDate),
                  });
                  // @dani multi-invoice deprecate
                  updateInvoice({
                    field: 'accrualEndDate',
                    value: commonDate.dateToFullDateString(newEndDate),
                  });

                  updateInvoiceComponent({
                    invoiceComponentIndex,
                    field: 'accrualStartDate',
                    value: commonDate.dateToFullDateString(newStartDate),
                  });
                  updateInvoiceComponent({
                    invoiceComponentIndex,
                    field: 'accrualEndDate',
                    value: commonDate.dateToFullDateString(newEndDate),
                  });

                  // whenever the accrual dates change, we want to reset all the invoice items
                  const newInvoiceItems: InvoiceItemInputGQLType[] = [];

                  updateInvoiceItemsAndTotalsForInvoiceComponent({
                    invoiceComponentIndex,
                    newInvoiceItemsForInvoiceComponent: newInvoiceItems,
                  });
                }
              }}
              currentDateRange={{
                startDate: commonDate.fullDateStrToDate(
                  invoiceComponentsWithInvoiceItems[invoiceComponentIndex]
                    .invoiceComponent.accrualStartDate,
                ),
                endDate: commonDate.fullDateStrToDate(
                  invoiceComponentsWithInvoiceItems[invoiceComponentIndex]
                    .invoiceComponent.accrualEndDate,
                ),
              }}
              maxDate={
                invoice.dueDate &&
                commonDate.fullDateStrToDate(invoice.dueDate) >
                  commonDate.fullDateStrToDate(loan.dates.maturityDate)
                  ? commonDate.fullDateStrToDate(invoice.dueDate)
                  : commonDate.fullDateStrToDate(loan.dates.maturityDate)
              }
              minDate={commonDate.fullDateStrToDate(loan.dates.originationDate)}
              showPreSelectedOptions={false}
            />
            {loan.invoices?.mostRecentAccrualEndDate &&
              invoiceComponentsWithInvoiceItems[invoiceComponentIndex]
                .invoiceComponent.accrualStartDate <
                loan.invoices.mostRecentAccrualEndDate && (
                <Alert status="warning" mt="16px">
                  <AlertIcon />
                  There is an existing invoice with an accrual end date of{' '}
                  {commonDate.fullDateStrToHumanDateString(
                    loan.invoices.mostRecentAccrualEndDate,
                  )}
                  .
                </Alert>
              )}
          </Box>
        )}

        <Box mb="24px">
          <CreateInvoiceLineItemsTable
            accrualsByTAccountMap={accrualsByTAccountMap}
            areAllComponentsFundedOnLoan={areAllComponentsFundedOnLoan}
            totalAccruedAndProjectedOutstandingBalanceAmount={
              totalAccruedAndProjectedOutstandingBalanceAmount
            }
            invoiceComponentIndex={invoiceComponentIndex}
          />
        </Box>
      </Flex>
    </Flex>
  );
};

export const CreateInvoiceLineItems = ({
  invoiceComponentIndex,
}: {
  invoiceComponentIndex: number;
}) => {
  const {
    invoiceComponentsWithInvoiceItems,
    selectedEntities: {
      creditFacility: { creditFacilityLoans },
    },
  } = useCreateInvoiceContext();

  const invoiceComponent =
    invoiceComponentsWithInvoiceItems[invoiceComponentIndex].invoiceComponent;
  const finleyLoanIdForInvoiceComponent = invoiceComponent.finleyLoanId;

  const loan = creditFacilityLoans?.find(
    (creditFacilityLoan) =>
      creditFacilityLoan.id === finleyLoanIdForInvoiceComponent,
  );

  // @dani todo: remove this once we implement support for facility-level invoices
  if (!loan) {
    return (
      <Flex direction="column" flexGrow={1}>
        <InfoAlertWithDescription description="No loan selected. Please select a loan to continue." />
      </Flex>
    );
  }

  return (
    <CreateLoanInvoiceLineItems
      loan={loan}
      invoiceComponentIndex={invoiceComponentIndex}
    />
  );
};
